import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Play, Star, Globe, Languages, ArrowDown, Sparkles, Heart, Moon, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import ContactForm from './ContactForm';
import EarlyAccessForm from './EarlyAccessForm';
import { useAnalytics } from './useAnalytics';

// Header Component - Ultra Modern
export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'home', href: '#accueil' },
    { key: 'about', href: '#about' },
    { key: 'features', href: '#fonctionnalites' },
    { key: 'testimonials', href: '#temoignages' },
    { key: 'faq', href: '#faq' }
  ];

  return (
    <motion.header 
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.08)] border-b border-white/20' 
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <motion.div 
            className="flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <div className="relative">
              <span className="text-3xl">☁️</span>
            </div>
            <span className="text-2xl font-black text-slate-600">
              123zzz
            </span>
          </motion.div>
          
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <motion.a
                key={item.key}
                href={item.href}
                className="relative text-slate-700 hover:text-slate-900 font-medium transition-colors duration-300"
                whileHover={{ y: -2 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {t(`header.${item.key}`)}
                <motion.div
                  className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.3 }}
                />
              </motion.a>
            ))}
          </nav>

          <div className="flex items-center space-x-6">
            <LanguageSelector />
          </div>
        </div>
      </div>
    </motion.header>
  );
};

// Hero Section - Ultra Modern with Glass Morphism
export const HeroSection = () => {
  const { t } = useTranslation();
  const { trackButtonClick } = useAnalytics();
  const [isEarlyAccessFormOpen, setIsEarlyAccessFormOpen] = useState(false);

  const handleGetStartedClick = () => {
    trackButtonClick('early_access_request');
    setIsEarlyAccessFormOpen(true);
  };

  return (
    <section id="accueil" className="min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-blue-400/30 to-purple-400/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-40 right-20 w-96 h-96 bg-gradient-to-r from-pink-400/20 to-blue-400/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-purple-400/10 to-blue-400/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            className="space-y-8 text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className="space-y-6 flex flex-col items-center lg:items-start">
              <h1 className="text-6xl lg:text-8xl font-black leading-[0.9] tracking-tight">
                <motion.span 
                  className="block text-slate-900"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                >
                  {t('hero.create')}
                </motion.span>
                <motion.span 
                  className="block bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                >
                  {t('hero.your_own')}
                </motion.span>
                <motion.span 
                  className="block bg-gradient-to-r from-orange-500 via-pink-500 to-red-500 bg-clip-text text-transparent"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.8 }}
                >
                  {t('hero.stories')}
                </motion.span>
              </h1>
              
              <motion.p 
                className="text-xl text-slate-600 font-medium"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                {t('hero.subtitle')}
              </motion.p>
            </div>

            <motion.p 
              className="text-lg text-slate-600 leading-relaxed max-w-lg mx-auto lg:mx-0 text-justify"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
            >
              {t('hero.description')}
            </motion.p>

            <motion.div 
              className="space-y-6 flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3 }}
            >


              {/* Bouton d'accès privilégié - plus gros */}
              <motion.div
                className="mt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
              >
                <motion.button
                  onClick={handleGetStartedClick}
                  className="inline-flex items-center space-x-4 px-12 py-6 bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 text-white rounded-3xl font-bold text-xl shadow-2xl hover:shadow-3xl transition-all duration-300 relative overflow-hidden"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  <Sparkles className="w-6 h-6 relative z-10" />
                  <span className="relative z-10">{t('hero.vip_access')}</span>
                  <motion.div 
                    className="bg-white/20 text-sm px-4 py-2 rounded-full font-medium relative z-10"
                    animate={{ 
                      scale: [1, 1.1, 1],
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    {t('hero.exclusive')}
                  </motion.div>
                </motion.button>
              </motion.div>

              <div className="flex items-center justify-center space-x-3">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.5 + i * 0.1 }}
                    >
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    </motion.div>
                  ))}
                </div>
                <span className="text-sm text-slate-600 font-medium">{t('hero.recommended')}</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
          >
            <div className="relative w-[400px] h-[400px] mx-auto">
              {/* Floating Elements */}
              <motion.div 
                className="absolute -top-8 -left-8 w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-2xl"
                animate={{ 
                  y: [0, -20, 0],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Sparkles className="w-8 h-8 text-white" />
              </motion.div>

              <motion.div 
                className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-pink-400 to-orange-500 rounded-full flex items-center justify-center shadow-2xl"
                animate={{ 
                  y: [0, -15, 0],
                  x: [0, 10, 0]
                }}
                transition={{ 
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              >
                <span className="text-xl">⭐</span>
              </motion.div>

              <motion.div 
                className="absolute -bottom-6 -left-6 w-14 h-14 bg-gradient-to-br from-green-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-2xl"
                animate={{ 
                  y: [0, -10, 0],
                  rotate: [0, -10, 10, 0]
                }}
                transition={{ 
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2
                }}
              >
                <Moon className="w-7 h-7 text-white" />
              </motion.div>

              <motion.div 
                className="absolute -bottom-2 -right-2 w-16 h-16 bg-gradient-to-br from-yellow-400 to-red-500 rounded-2xl flex items-center justify-center shadow-2xl"
                animate={{ 
                  y: [0, -12, 0],
                  rotate: [0, 8, -8, 0],
                  scale: [1, 1.05, 1]
                }}
                transition={{ 
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              >
                <Moon className="w-8 h-8 text-white" />
              </motion.div>

              {/* Main Image with Glass Morphism */}
              <div className="relative w-full h-full">
                <div className="absolute inset-0 bg-white/30 backdrop-blur-sm rounded-3xl border border-white/20 shadow-2xl"></div>
                <div className="absolute inset-2 rounded-2xl overflow-hidden">
                  <img 
                    src="https://res.cloudinary.com/dogguvwef/image/upload/v1751436108/dream_dborgw.png"
                    alt="Rêves et nuages"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent"></div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center space-y-2"
          >
            <span className="text-sm text-slate-500 font-medium">Découvrir</span>
            <ArrowDown className="w-5 h-5 text-slate-400" />
          </motion.div>
        </motion.div>
      </div>

      {/* Early Access Form Modal */}
      <EarlyAccessForm 
        isOpen={isEarlyAccessFormOpen} 
        onClose={() => setIsEarlyAccessFormOpen(false)} 
      />
    </section>
  );
};

// Media Section - Modern Glass Cards
export const MediaSection = () => {
  const mediaLogos = [
    { name: "WIRED", color: "from-red-500 to-pink-500" },
    { name: "PULS 4", color: "from-blue-500 to-purple-500" },
    { name: "DIE ZEIT", color: "from-green-500 to-teal-500" },
    { name: "Focus", color: "from-orange-500 to-red-500" },
    { name: "Kronen Zeitung", color: "from-purple-500 to-indigo-500" }
  ];

  return (
    <section className="py-20 bg-white/50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-slate-700 mb-12">
            Ils parlent de <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">123zzz</span>
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12">
            {mediaLogos.map((logo, index) => (
              <motion.div
                key={logo.name}
                className="group relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1, y: -5 }}
              >
                <div className="px-6 py-3 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <div className={`text-lg font-bold bg-gradient-to-r ${logo.color} bg-clip-text text-transparent`}>
                    {logo.name}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Favorite Stories Section - Modern Story Reader
export const FavoriteStoriesSection = () => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const { t } = useTranslation();
  
  const storyPhases = [
    {
      image: "https://res.cloudinary.com/dogguvwef/image/upload/v1751434685/Luna1_hze4ue.png",
      text: t('favorite_stories.story_phase_1')
    },
    {
      image: "https://res.cloudinary.com/dogguvwef/image/upload/v1751434687/Luna2_kx3bgd.png", 
      text: t('favorite_stories.story_phase_2')
    },
    {
      image: "https://res.cloudinary.com/dogguvwef/image/upload/v1751434683/Luna4_lfjmri.webp",
      text: t('favorite_stories.story_phase_3')
    },
    {
      image: "https://res.cloudinary.com/dogguvwef/image/upload/v1751434683/Luna6_axiuwj.webp",
      text: t('favorite_stories.story_phase_4')
    }
  ];

  const nextPhase = () => {
    setCurrentPhase((prev) => (prev + 1) % storyPhases.length);
  };

  const prevPhase = () => {
    setCurrentPhase((prev) => (prev - 1 + storyPhases.length) % storyPhases.length);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-purple-50/50 to-blue-50/30">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="inline-block mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider shadow-lg">
              {t('favorite_stories.badge')}
            </span>
          </motion.div>
          
          <h2 className="text-4xl lg:text-6xl font-black mb-6 leading-tight">
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {t('favorite_stories.title')}
            </span>
          </h2>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-12 text-justify">
            {t('favorite_stories.description')}
          </p>

          {/* Cover Image */}
          <motion.div 
            className="relative max-w-lg mx-auto mb-12"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 to-pink-400/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-white/60 backdrop-blur-sm rounded-3xl p-4 border border-white/20 shadow-2xl">
                <img 
                  src="https://res.cloudinary.com/dogguvwef/image/upload/v1751434687/Luna_Intro_ogr3ry.png"
                  alt="Luna et l'Étoile Magique - Couverture"
                  className="w-full h-96 object-cover rounded-2xl"
                  loading="eager"
                  style={{ imageRendering: 'auto' }}
                />
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="relative bg-white/60 backdrop-blur-sm rounded-[2rem] border border-white/20 shadow-2xl overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-purple-50/50 to-pink-50/50"></div>
          
          {/* Story Image */}
          <div className="relative p-8">
            <motion.div 
              className="relative mb-8"
              key={currentPhase}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-blue-50 to-purple-50">
                <img 
                  src={storyPhases[currentPhase].image}
                  alt={`Luna - Phase ${currentPhase + 1}`}
                  className="w-full h-full object-contain"
                  loading="eager"
                  style={{ imageRendering: 'auto' }}
                />
              </div>
            </motion.div>

            {/* Story Content */}
            <motion.div 
              className="bg-gradient-to-r from-orange-100/80 to-yellow-100/80 backdrop-blur-sm rounded-2xl p-8 border-l-4 border-orange-400 shadow-lg"
              key={`content-${currentPhase}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <p className="text-slate-700 leading-relaxed text-lg text-justify">
                {storyPhases[currentPhase].text}
              </p>
            </motion.div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8">
              <motion.button
                onClick={prevPhase}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                whileHover={{ scale: 1.02, x: -2 }}
                whileTap={{ scale: 0.98 }}
                disabled={currentPhase === 0}
              >
                <span className="text-2xl text-white group-hover:-translate-x-1 transition-transform">←</span>
                <span className="font-medium text-white">{t('favorite_stories.previous_page')}</span>
              </motion.button>

              {/* Page Indicators */}
              <div className="flex space-x-3">
                {storyPhases.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setCurrentPhase(index)}
                    className={`w-4 h-4 rounded-full transition-all duration-300 ${
                      index === currentPhase 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 scale-125' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  />
                ))}
              </div>

              <motion.button
                onClick={nextPhase}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                whileHover={{ scale: 1.02, x: 2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="font-medium">{t('favorite_stories.next_page')}</span>
                <span className="text-2xl group-hover:translate-x-1 transition-transform">→</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};
// AI Story Generator Section
export const AIStorySection = () => {
  const { t } = useTranslation();

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-block mb-8"
            whileHover={{ scale: 1.05 }}
          >
            <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider shadow-lg">
              {t('story_generator.trending')}
            </span>
          </motion.div>
          
          <h2 className="text-5xl lg:text-7xl font-black mb-8 leading-tight">
            {t('story_generator.generator_title')}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('story_generator.generator_subtitle')}</span>
            <br />
            <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">{t('story_generator.infinite')}</span>
          </h2>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-16 leading-relaxed">
            {t('story_generator.description')}
          </p>
        </motion.div>

        {/* Teaching Section intégrée */}
        <div className="mb-20">
          <motion.div 
            className="relative overflow-hidden rounded-[2rem]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700"></div>
            
            {/* Animated Background Elements */}
            <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-48 -translate-y-48 animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl translate-x-40 translate-y-40 animate-pulse" style={{animationDelay: '2s'}}></div>
            
            <div className="relative z-10 p-12 lg:p-16">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                  <h3 className="text-4xl lg:text-6xl font-black text-white leading-tight">
                    {t('story_generator.teaching_title')}
                  </h3>
                  <p className="text-purple-100 text-lg leading-relaxed text-justify">
                    {t('story_generator.teaching_description')}
                  </p>
                  
                  <div className="flex flex-wrap gap-3">
                    {[t('story_generator.honesty'), t('story_generator.kindness'), t('story_generator.courage'), t('story_generator.empathy'), t('story_generator.responsibility')].map((value, index) => (
                      <motion.span
                        key={value}
                        className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-medium text-sm border border-white/30"
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        viewport={{ once: true }}
                        whileHover={{ scale: 1.05 }}
                      >
                        {value}
                      </motion.span>
                    ))}
                  </div>
                </div>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-white/20 backdrop-blur-sm rounded-full border border-white/30"></div>
                    <img 
                      src="https://res.cloudinary.com/dogguvwef/image/upload/v1751436099/Kid_ge2ziw.png"
                      alt="Enfant heureux apprenant"
                      className="relative z-10 w-80 h-80 rounded-full object-cover mx-auto shadow-2xl"
                    />
                    
                    {/* Floating Icons */}
                    <motion.div 
                      className="absolute -top-4 -right-4 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30"
                      animate={{ y: [0, -10, 0], rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 4, repeat: Infinity }}
                    >
                      <Heart className="w-8 h-8 text-pink-300" />
                    </motion.div>
                    
                    <motion.div 
                      className="absolute -bottom-4 -left-4 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30"
                      animate={{ y: [0, -8, 0], x: [0, 5, 0] }}
                      transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                    >
                      <Star className="w-7 h-7 text-yellow-300 fill-current" />
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Animated Stories Section - Mini Movies Feature
export const AnimatedStoriesSection = () => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Video Section */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-white/60 backdrop-blur-sm rounded-3xl p-6 border border-white/20 shadow-2xl">
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <video 
                    src="https://res.cloudinary.com/dogguvwef/video/upload/v1751436905/VideoLuna_anp9ps.mp4"
                    controls
                    poster="https://res.cloudinary.com/dogguvwef/image/upload/v1751434687/Luna_Intro_ogr3ry.png"
                    className="w-full h-auto rounded-2xl"
                    preload="metadata"
                  >
                    Votre navigateur ne supporte pas la vidéo.
                  </video>
                  
                  {/* Floating Badge */}
                  <motion.div 
                    className="absolute top-4 left-4 bg-gradient-to-r from-pink-500 to-red-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1, type: "spring", stiffness: 200 }}
                  >
                    {t('animated_stories.preview')}
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8 order-1 lg:order-2"
          >
            <div className="space-y-8">
              <motion.div
                className="inline-block"
                whileHover={{ scale: 1.05 }}
              >
                <span className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider shadow-lg">
                  {t('animated_stories.new')}
                </span>
              </motion.div>
              
              <div className="space-y-4">
                <h2 className="text-5xl lg:text-6xl font-black leading-tight text-slate-900">
                  {t('animated_stories.title')}
                  <br />
                  <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                    {t('animated_stories.subtitle')}
                  </span>
                </h2>
                
                <p className="text-2xl text-slate-500 leading-relaxed font-light">
                  {t('animated_stories.tagline')}
                </p>
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-white/40 shadow-xl">
                <p className="text-lg text-slate-700 leading-relaxed text-justify">
                  {t('animated_stories.description')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Quote - Simplified */}
        <motion.div 
          className="text-center mt-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-8 border border-blue-100 shadow-lg">
            <p className="text-xl font-medium text-slate-700 leading-relaxed italic">
              {t('animated_stories.quote')}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Audio Stories Section - Modern Layout
export const AudioStoriesSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-white to-blue-50/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <motion.div
                className="inline-block"
                whileHover={{ scale: 1.05 }}
              >
                <span className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide">
                  ✨ Nouveau
                </span>
              </motion.div>
              
              <h2 className="text-5xl lg:text-6xl font-black leading-tight">
                Histoires Audio
                <br />
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Personnalisées
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 leading-relaxed">
                Avec la dernière technologie IA, les histoires personnalisées peuvent maintenant être transformées 
                en livres audio. Transformez les aventures magiques de vos enfants en expériences d'écoute uniques avec des voix 
                agréables et apaisantes qui stimulent l'imagination et font de l'endormissement un plaisir.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-3xl blur-2xl opacity-20"></div>
              <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-3 border border-white/20 shadow-2xl">
                <div className="relative rounded-2xl overflow-hidden group">
                  <img 
                    src="https://images.pexels.com/photos/14272792/pexels-photo-14272792.jpeg"
                    alt="Histoires Audio"
                    className="w-full h-96 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <motion.button 
                    className="absolute inset-0 flex items-center justify-center group-hover:bg-black/20 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 shadow-2xl group-hover:bg-white transition-all duration-300">
                      <Play className="w-8 h-8 text-blue-600 ml-1" />
                    </div>
                  </motion.button>
                  
                  {/* Audio Visualizer */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex space-x-1">
                          {[...Array(20)].map((_, i) => (
                            <motion.div
                              key={i}
                              className="w-1 bg-white rounded-full"
                              animate={{ 
                                height: [8, Math.random() * 32 + 8, 8] 
                              }}
                              transition={{ 
                                duration: 1.5, 
                                repeat: Infinity, 
                                delay: i * 0.1 
                              }}
                            />
                          ))}
                        </div>
                        <span className="text-white text-sm font-medium">Histoire en cours...</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Teaching Section - Glass Morphism Card
export const TeachingSection = () => {
  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          className="relative overflow-hidden rounded-[2rem]"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700"></div>
          
          {/* Animated Background Elements */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-48 -translate-y-48 animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl translate-x-40 translate-y-40 animate-pulse" style={{animationDelay: '2s'}}></div>
          
          <div className="relative z-10 p-12 lg:p-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <h2 className="text-4xl lg:text-6xl font-black text-white leading-tight">
                  Enseigner aux enfants
                  <br />
                  des leçons précieuses
                </h2>
                <p className="text-purple-100 text-lg leading-relaxed text-justify">
                  123zzz génère des histoires qui ne sont pas seulement divertissantes mais aussi intégrées avec des leçons. 
                  Elles offrent une large gamme d'histoires qui couvrent divers sujets comme l'honnêteté, la gentillesse, 
                  le courage, l'empathie et la responsabilité, entre autres. Avec 123zzz, les enfants peuvent apprendre 
                  des valeurs importantes de manière amusante et excitante.
                </p>
                
                <div className="flex flex-wrap gap-3">
                  {['Honnêteté', 'Gentillesse', 'Courage', 'Empathie', 'Responsabilité'].map((value, index) => (
                    <motion.span
                      key={value}
                      className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-medium text-sm border border-white/30"
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.05 }}
                    >
                      {value}
                    </motion.span>
                  ))}
                </div>
              </div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-white/20 backdrop-blur-sm rounded-full border border-white/30"></div>
                  <img 
                    src="https://images.pexels.com/photos/1108193/pexels-photo-1108193.jpeg"
                    alt="Enfant heureux apprenant"
                    className="relative z-10 w-80 h-80 rounded-full object-cover mx-auto shadow-2xl"
                  />
                  
                  {/* Floating Icons */}
                  <motion.div 
                    className="absolute -top-4 -right-4 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30"
                    animate={{ y: [0, -10, 0], rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 4, repeat: Infinity }}
                  >
                    <Heart className="w-8 h-8 text-pink-300" />
                  </motion.div>
                  
                  <motion.div 
                    className="absolute -bottom-4 -left-4 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30"
                    animate={{ y: [0, -8, 0], x: [0, 5, 0] }}
                    transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                  >
                    <Star className="w-7 h-7 text-yellow-300 fill-current" />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// App Features Section - Modern Cards Grid
export const AppFeaturesSection = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Users,
      title: t('app_features.card1_title'),
      description: t('app_features.card1_description'),
      gradient: "from-blue-500 to-purple-500"
    },
    {
      icon: Heart,
      title: t('app_features.card2_title'),
      description: t('app_features.card2_description'),
      gradient: "from-pink-500 to-red-500"
    },
    {
      icon: Moon,
      title: t('app_features.card3_title'),
      description: t('app_features.card3_description'),
      gradient: "from-indigo-500 to-blue-500"
    }
  ];

  return (
    <section id="fonctionnalites" className="py-24 bg-gradient-to-b from-blue-50/50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl lg:text-6xl font-black mb-16 leading-tight">
            {t('app_features.title')}{' '}
            <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">{t('app_features.story')}</span>
            <br />
            {t('app_features.every_night')}
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="space-y-8 text-center">
              <ul className="space-y-6">
                {[
                  t('app_features.feature1'),
                  t('app_features.feature2'),
                  t('app_features.feature3'),
                  t('app_features.feature4')
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start space-x-4 justify-center"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-3 h-3 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full mt-2 shrink-0"></div>
                    <p className="text-slate-600 leading-relaxed text-justify max-w-lg">{item}</p>
                  </motion.li>
                ))}
              </ul>
            </div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-2xl opacity-20"></div>
                <div className="relative bg-white/60 backdrop-blur-sm rounded-3xl p-6 border border-white/20 shadow-2xl">
                  <img 
                    src="https://res.cloudinary.com/dogguvwef/image/upload/v1751436109/Voice_ioat3p.png"
                    alt="Téléphone avec histoires du coucher"
                    className="w-full max-w-sm mx-auto rounded-2xl"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
            >
              <div className="relative h-full">
                <div className="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-3xl border border-white/20 shadow-xl group-hover:shadow-2xl transition-all duration-300"></div>
                {/* Badge "Soon" pour la troisième carte */}
                {index === 2 && (
                  <motion.div
                    className="absolute -top-2 -right-2 z-10 bg-gradient-to-r from-orange-400 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg"
                    initial={{ scale: 0 }}
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    {t('app_features.soon')}
                  </motion.div>
                )}
                <div className="relative p-6 lg:p-8 space-y-4 text-center h-full flex flex-col justify-between min-h-[320px]">
                  <div className={`w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br ${feature.gradient} rounded-2xl lg:rounded-3xl flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                  </div>
                  <h4 className="text-lg lg:text-xl xl:text-2xl font-bold text-slate-800 leading-tight px-2">
                    {feature.title}
                  </h4>
                  <p className="text-sm lg:text-base text-slate-600 leading-relaxed px-2 flex-grow flex items-center justify-center text-left text-justify">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section - Modern Grid
export const TestimonialsSection = () => {
  const { t } = useTranslation();

  const testimonials = [
    {
      name: "Sophie M.",
      text: t('testimonials.testimonial_1'),
      rating: 5,
      gradient: "from-blue-500 to-purple-500"
    },
    {
      name: "Pierre L.",
      text: t('testimonials.testimonial_2'),
      rating: 5,
      gradient: "from-purple-500 to-pink-500"
    },
    {
      name: "Claire D.",
      text: t('testimonials.testimonial_3'),
      rating: 5,
      gradient: "from-pink-500 to-red-500"
    },
    {
      name: "Thomas G.",
      text: t('testimonials.testimonial_4'),
      rating: 5,
      gradient: "from-green-500 to-teal-500"
    },
    {
      name: "Julie R.",
      text: t('testimonials.testimonial_5'),
      rating: 5,
      gradient: "from-orange-500 to-yellow-500"
    },
    {
      name: "Emma B.",
      text: t('testimonials.testimonial_6'),
      rating: 5,
      gradient: "from-indigo-500 to-blue-500"
    }
  ];

  return (
    <section id="temoignages" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl lg:text-6xl font-black mb-4">
            {t('testimonials.title')}{' '}
            <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">{t('testimonials.parents')}</span>
            {t('testimonials.say')}
          </h2>
          <p className="text-xl text-slate-600">{t('testimonials.subtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="relative h-full">
                <div className={`absolute inset-0 bg-gradient-to-br ${testimonial.gradient} rounded-3xl opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-white/20 shadow-xl group-hover:shadow-2xl transition-all duration-300 h-full flex flex-col">
                  <div className="flex items-center space-x-2 mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      </motion.div>
                    ))}
                  </div>
                  
                  <p className="text-slate-600 mb-8 text-sm leading-relaxed flex-grow">
                    "{testimonial.text}"
                  </p>
                  
                  <div className="flex items-center justify-center">
                    <div>
                      <span className="font-bold text-slate-800">{testimonial.name}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
export const FAQSection = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const faqs = [
    {
      question: t('faq.question1'),
      answer: t('faq.answer1')
    },
    {
      question: t('faq.question2'),
      answer: t('faq.answer2')
    },
    {
      question: t('faq.question3'),
      answer: t('faq.answer3')
    },
    {
      question: t('faq.question4'),
      answer: t('faq.answer4')
    },
    {
      question: t('faq.question5'),
      answer: t('faq.answer5')
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  return (
    <section id="faq" className="py-24 bg-gradient-to-b from-white to-blue-50/30">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="inline-block mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full text-lg font-bold uppercase tracking-wider shadow-lg">
              {t('faq.badge')}
            </span>
          </motion.div>
          
          <h2 className="text-4xl lg:text-6xl font-black mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {t('faq.title')}
            </span>
          </h2>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-white/50 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold text-slate-800 pr-4">
                  {faq.question}
                </h3>
                <div className={`text-blue-500 text-2xl transform transition-transform duration-300 ${
                  openIndex === index ? 'rotate-45' : ''
                }`}>
                  +
                </div>
              </button>
              
              <motion.div
                initial={false}
                animate={{
                  height: openIndex === index ? 'auto' : 0,
                  opacity: openIndex === index ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-5 text-slate-600 leading-relaxed">
                  {faq.answer}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 rounded-3xl p-8 border border-blue-100">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">
              {t('faq.contact_title')}
            </h3>
            <p className="text-slate-600 mb-6">
              {t('faq.contact_description')}
            </p>
            <button 
              onClick={openContactModal}
              className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105"
            >
              {t('faq.contact_button')}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Contact Modal */}
      <ContactForm 
        isOpen={isContactModalOpen} 
        onClose={closeContactModal} 
      />
    </section>
  );
};

// Supporters Section - Partnership Display
export const SupportersSection = () => {
  const { t } = useTranslation();

  const supporters = [
    { 
      name: "Tech Ready Women", 
      logo: "https://res.cloudinary.com/dogguvwef/image/upload/v1751443995/U8E4zNlrTy2RIRH7GeYE_TRW_logo_BY_large_3_xiusiu.webp",
      description: "Women in Tech" 
    },
    { 
      name: "Advanced Queensland", 
      logo: "https://res.cloudinary.com/dogguvwef/image/upload/v1751443995/thumbnail_ihptnp.jpg",
      description: "Innovation Queensland" 
    },
    { 
      name: "Queensland Government", 
      logo: "https://res.cloudinary.com/dogguvwef/image/upload/v1751443995/20250506-queensland-gov_cuamc2.png",
      description: "Gouvernement du Queensland" 
    },
    { 
      name: "Female Founders Program", 
      logo: "https://res.cloudinary.com/dogguvwef/image/upload/v1751443995/Female_Founders_Program_logo_gc3ixj.png",
      description: "Programme Female Founders" 
    },
    { 
      name: "Startup Support", 
      logo: "https://res.cloudinary.com/dogguvwef/image/upload/v1751443995/0b14cd4-b6e-4e6a-b37-1ce60be30e_Logos_paqk9f.webp",
      description: "Programme d'accélération" 
    }
  ];

  return (
    <section id="partenaires" className="py-32 bg-gradient-to-b from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="inline-block mb-8"
            whileHover={{ scale: 1.05 }}
            animate={{ 
              y: [0, -10, 0],
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <span className="bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 text-white px-8 py-4 rounded-full text-lg font-black uppercase tracking-wider shadow-2xl">
              {t('supporters.innovation')}
            </span>
          </motion.div>
          
          <h2 className="text-6xl lg:text-8xl font-black leading-tight mb-8">
            {t('supporters.title')}
            <br />
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              {t('supporters.global_leaders')}
            </span>
          </h2>
          
          <p className="text-2xl lg:text-3xl text-slate-600 max-w-4xl mx-auto leading-relaxed font-medium">
            {t('supporters.subtitle')} 
            <span className="text-blue-600 font-bold"> {t('supporters.disruptive')}</span>
            {t('supporters.approach')}
          </p>
        </motion.div>

        {/* Carrousel horizontal des supporters */}
        <div className="relative overflow-hidden mb-16">
          <motion.div 
            className="flex space-x-12 lg:space-x-16"
            animate={{ 
              x: [0, -100 * supporters.length * 2] 
            }}
            transition={{ 
              duration: 30,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{ width: `${supporters.length * 400}px` }}
          >
            {[...supporters, ...supporters, ...supporters].map((supporter, index) => (
              <motion.div
                key={`${supporter.name}-${index}`}
                className="flex-shrink-0 group relative"
                whileHover={{ scale: 1.1 }}
              >
                <div className="relative w-48 h-32 lg:w-56 lg:h-36">
                  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-3xl border border-white/40 shadow-2xl group-hover:shadow-3xl group-hover:bg-white/95 transition-all duration-500"></div>
                  <div className="relative p-4 lg:p-6 h-full flex flex-col items-center justify-center">
                    {/* Container pour le logo avec taille fixe */}
                    <div className="w-full h-20 lg:h-24 flex items-center justify-center mb-2">
                      <img 
                        src={supporter.logo}
                        alt={supporter.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="text-slate-500 text-xs lg:text-sm font-medium text-center">
                      {supporter.description}
                    </div>
                  </div>
                  
                  {/* Effet de brillance au hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Gradient de fade sur les côtés */}
          <div className="absolute top-0 left-0 w-20 h-full bg-gradient-to-r from-white to-transparent pointer-events-none z-10"></div>
          <div className="absolute top-0 right-0 w-20 h-full bg-gradient-to-l from-white to-transparent pointer-events-none z-10"></div>
        </div>

        {/* Message d'impact */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 rounded-3xl p-8 lg:p-12 border border-blue-100 shadow-xl">
            <h3 className="text-3xl lg:text-4xl font-black text-slate-800 mb-6">
              {t('supporters.ecosystem_title')}
            </h3>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed max-w-4xl mx-auto text-justify">
              {t('supporters.ecosystem_description')}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section supprimée - utilisateur l'a demandé

// Footer Component - Modern & Clean
export const Footer = () => {
  const footerSections = {
    pages: ["Accueil", "À propos", "Témoignages", "FAQ", "Blog", "Scores de Lisibilité", "Histoires d'Enfants"],
    storiesForChildren: [
      "Histoires Gratuites du Coucher",
      "Histoires Scientifiques Pour Enfants", 
      "Histoires Sci-Fi pour Enfants",
      "Histoires de Chantier Amusantes",
      "Histoires sur le Changement Climatique",
      "Histoires Courtes Amusantes",
      "Histoires de Noël",
      "Histoires Éducatives"
    ],
    legal: ["Politique de Confidentialité", "Conditions d'Utilisation", "Mentions Légales"],
    partners: ["BrandingS", "Frog das PDF"],
    about: ["Contact", "Instagram", "Twitter", "LinkedIn", "Kit Presse"]
  };

  const languages = [
    { code: "FR", flag: "🇫🇷", name: "Français" },
    { code: "EN", flag: "🇺🇸", name: "English" },
    { code: "DE", flag: "🇩🇪", name: "Deutsch" },
    { code: "ES", flag: "🇪🇸", name: "Español" },
    { code: "IT", flag: "🇮🇹", name: "Italiano" },
    { code: "PT", flag: "🇵🇹", name: "Português" }
  ];

  return (
    <footer className="bg-slate-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-purple-500 rounded-full blur-3xl translate-x-40 translate-y-40"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        {/* Logo & Description */}
        <div className="text-center mb-16">
          <motion.div 
            className="flex justify-center items-center space-x-3 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">☁</span>
            </div>
            <span className="text-3xl font-black bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              123zzz
            </span>
          </motion.div>
          <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Créez des histoires personnalisées magiques pour vos enfants grâce à une IA puissante. 
            Transformez l'heure du coucher en aventures inoubliables.
          </p>
        </div>

        {/* Links Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-16">
          <div>
            <h3 className="font-bold text-lg mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Pages
            </h3>
            <ul className="space-y-3">
              {footerSections.pages.map((item, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transition-transform duration-200">
                    {item}
                  </a>
                </motion.li>
              ))}
            </ul>
          </div>
          
          <div className="lg:col-span-2">
            <h3 className="font-bold text-lg mb-6 bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
              Histoires pour Enfants
            </h3>
            <ul className="space-y-3">
              {footerSections.storiesForChildren.map((item, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transition-transform duration-200">
                    {item}
                  </a>
                </motion.li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-6 bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
              Légal
            </h3>
            <ul className="space-y-3">
              {footerSections.legal.map((item, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transition-transform duration-200">
                    {item}
                  </a>
                </motion.li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-6 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
              À propos
            </h3>
            <ul className="space-y-3">
              {footerSections.about.map((item, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm hover:translate-x-1 inline-block transition-transform duration-200">
                    {item}
                  </a>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        {/* Language Selector */}
        <div className="border-t border-slate-700 pt-12 mb-12">
          <div className="text-center mb-8">
            <h4 className="text-slate-300 font-semibold mb-6">Disponible en plusieurs langues</h4>
            <div className="flex flex-wrap justify-center gap-3">
              {languages.map((lang, index) => (
                <motion.button
                  key={index}
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-600 hover:border-slate-500 transition-all duration-300 text-sm group"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05, y: -2 }}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="group-hover:text-white transition-colors">{lang.name}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Bottom Section */}
        <div className="border-t border-slate-700 pt-8">
          <div className="text-center space-y-6">
            <div className="space-y-4">
              <p className="text-slate-400 text-sm font-medium">Financé par</p>
              <div className="flex flex-wrap justify-center items-center gap-6 text-slate-500 text-sm">
                <span>Ministère Fédéral République d'Autriche</span>
                <span>•</span>
                <span>Affaires Numériques et Économiques</span>
                <span>•</span>
                <span>AWS</span>
              </div>
            </div>
            
            <div className="space-y-4">
              <p className="text-slate-400 text-sm font-medium">Soutenu par</p>
              <div className="flex flex-wrap justify-center items-center gap-6 text-slate-500 text-sm">
                <span>AUSTRIA STARTUP ACADEMY</span>
                <span>•</span>
                <span>Google for Startups</span>
              </div>
            </div>
            
            <div className="pt-8 border-t border-slate-800">
              <p className="text-slate-500 text-sm">
                Fait avec <span className="text-red-400 animate-pulse">❤️</span> en Autriche par{' '}
                <span className="text-white font-medium">Dima Rubanov</span> &{' '}
                <span className="text-white font-medium">Matthias Neumayer</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};